import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { reactQueryClient } from "@redotech/redo-web/rpc-query/react-query-config";
import { useQuery } from "@tanstack/react-query";
import { RedoPlatformAuthRpcClientContext } from "../platform/redo-platform-rpc-client-provider";
import { TeamContext } from "../team";

export interface SupportHelpdeskV2EnabledState {
  readonly pending: boolean;
  readonly enabled: boolean;
}

const supportHelpdeskV2QueryKeyPrefix = [
  "featureFlag",
  FeatureFlagKey.SUPPORT_HELPDESK_V2,
] as const;

export function supportHelpdeskV2EnabledQueryKey(teamId: string) {
  return [...supportHelpdeskV2QueryKeyPrefix, teamId] as const;
}

export function invalidateSupportHelpdeskV2Enabled(teamId?: string): void {
  void reactQueryClient.invalidateQueries({
    queryKey: teamId
      ? supportHelpdeskV2EnabledQueryKey(teamId)
      : supportHelpdeskV2QueryKeyPrefix,
  });
}

/**
 * Loads the `SUPPORT_HELPDESK_V2` feature flag for the current team.
 *
 * While `pending`, callers should render normally but skip any dual-write
 * side effects that depend on the flag — they'll resolve on the next save.
 * Once resolved, React Query caches the value per team across consumers.
 */
export function useSupportHelpdeskV2Enabled(): SupportHelpdeskV2EnabledState {
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );
  const team = useRequiredContext(TeamContext);
  const teamId = String(team._id);

  const query = useQuery({
    queryKey: supportHelpdeskV2EnabledQueryKey(teamId),
    queryFn: async () => {
      const response = await platformRpcClient.getFeatureFlag({
        flagKey: FeatureFlagKey.SUPPORT_HELPDESK_V2,
      });
      return response.enabled;
    },
    staleTime: Infinity,
  });

  return { pending: query.isPending, enabled: query.data ?? false };
}
